import { useId, useState } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import type {
  TachoCardCounters,
  TachoDriverCardData,
} from "@/lib/aiWidgetTachoParser"
import {
  bindingCounter,
  drivingBeforeRest,
  driverNextStop,
  formatClock,
  formatMin,
  formatWhen,
  type StopReason,
} from "./tachoCardFormat"
import { ACTIVITY_ICON, TachoSourceFooter } from "./TachoCardParts"
import { useTachoCardStrings } from "./tachoCardStrings"

const COUNTERS: Array<keyof TachoCardCounters> = [
  "breakMin",
  "todayMin",
  "weekMin",
  "twoWeeksMin",
]

function Tile({
  title,
  badge,
  label,
  value,
  note,
}: {
  title: string
  badge?: string | null
  label?: string | null
  value?: string | null
  note?: string | null
}) {
  const titleId = useId()
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className="rounded-lg bg-muted px-3 py-2.5"
    >
      <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold">
        <span id={titleId}>{title}</span>
        {badge && (
          <span className="rounded-full bg-foreground px-2 text-xs font-bold text-background">
            {badge}
          </span>
        )}
      </div>
      {label && (
        <div className="mt-1.5 text-xs text-muted-foreground">{label}</div>
      )}
      {value && <div className="text-xl font-semibold">{value}</div>}
      {note && <div className="mt-0.5 text-xs">{note}</div>}
    </div>
  )
}

/** The length of the break, or the limit that ran out, in minutes. */
const STOP_LENGTH_MIN: Record<StopReason, number | null> = {
  break: 45,
  // The daily limit is 9 h or 10 h; the tachograph does not say which today.
  today: null,
  week: 56 * 60,
  twoWeeks: 90 * 60,
}

function NextStop({ driver }: { driver: TachoDriverCardData }) {
  const t = useTachoCardStrings()
  const next = driverNextStop(driver)
  const crew = !!driver.crew
  switch (next.kind) {
    case "weeklyRest":
      return null
    case "stop": {
      const length = STOP_LENGTH_MIN[next.reason]
      return (
        <Tile
          title={t.stopTitle[next.reason]}
          badge={length === null ? null : formatMin(length)}
          label={
            crew
              ? t.crewStopBy
              : next.reason === "break"
                ? t.mustStartBy
                : t.mustStopBy
          }
          value={formatWhen(next.at, t.weekdays)}
          note={
            crew && driver.swapAt
              ? t.swap(formatClock(driver.swapAt))
              : t.inDriving(formatMin(next.inMin)) +
                (next.reason === "today" ? t.thenDailyRest : "")
          }
        />
      )
    }
    case "left":
      return (
        <Tile
          title={t.canDriveTitle}
          value={formatMin(next.min)}
          note={crew ? t.crewBudget : null}
        />
      )
    case "exhausted":
      return (
        <Tile
          title={t.canDriveTitle}
          value={formatMin(0)}
          note={next.counter && t.exhausted(t.driverCounters[next.counter])}
        />
      )
    case "unknown":
      return <Tile title={t.canDriveTitle} value="–" />
  }
}

function WeeklyRest({ driver }: { driver: TachoDriverCardData }) {
  const t = useTachoCardStrings()
  const { rest } = driver
  const badge = rest.requiredMin === null ? null : formatMin(rest.requiredMin)
  if (rest.inProgress) {
    return (
      <Tile
        title={t.weeklyRest}
        badge={badge}
        label={t.restLastsUntil}
        value={rest.endsAt ? formatWhen(rest.endsAt, t.weekdays) : "–"}
      />
    )
  }
  // No deadline: the sentence above the card already says so.
  if (!rest.dueAt) return null
  const before = drivingBeforeRest(driver)
  const notes = [
    before === null ? null : t.untilRest(formatMin(before)),
    driver.remainingDaysOfDriving === 0 ? t.lastDay : null,
  ].filter(Boolean)
  return (
    <Tile
      title={t.weeklyRest}
      badge={badge}
      label={t.mustStartBy}
      value={formatWhen(rest.dueAt, t.weekdays)}
      note={notes.join(" · ") || null}
    />
  )
}

export function TachoDriverCard({
  driver,
  now,
}: {
  driver: TachoDriverCardData
  now: number
}) {
  const t = useTachoCardStrings()
  const [open, setOpen] = useState(false)
  const Icon = ACTIVITY_ICON[driver.activity]
  const binding = bindingCounter(driver.counters)
  return (
    <div
      data-testid="tacho-driver-card"
      className="mt-3 rounded-xl border border-border bg-card p-4"
    >
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">{driver.plate}</span>
          {driver.name && ` · ${driver.name}`}
        </span>
        <span className="flex items-center gap-1.5">
          <Icon className="h-4 w-4" />
          {t.activities[driver.activity]}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <NextStop driver={driver} />
        <WeeklyRest driver={driver} />
      </div>

      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="mt-3 flex items-center gap-1 text-sm text-primary"
      >
        {open ? t.hideCounters : t.showCounters}
        <ChevronDown
          className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 text-sm">
          {COUNTERS.map((key) => {
            const value = driver.counters[key]
            const binds = key === binding
            return (
              <div key={key} className="flex justify-between gap-4">
                <span className={cn(!binds && "text-muted-foreground")}>
                  {t.driverCounters[key]}
                </span>
                <span className={cn("tabular-nums", binds && "font-semibold")}>
                  {value === null ? t.noData : t.left(formatMin(value))}
                </span>
              </div>
            )
          })}
          {!driver.rest.inProgress && driver.rest.endsAt && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">{t.restEndsBy}</span>
              <span>{formatWhen(driver.rest.endsAt, t.weekdays)}</span>
            </div>
          )}
          {driver.reducedDailyRestsLeft !== null && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">{t.reducedRests}</span>
              <span>{t.reducedRestsLeft(driver.reducedDailyRestsLeft)}</span>
            </div>
          )}
        </div>
      )}

      <TachoSourceFooter sourceAt={driver.sourceAt} now={now} />
    </div>
  )
}
